/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { ActionReducerMapBuilder, createReducer } from "@reduxjs/toolkit";

import { ViewTypeActionProps, ViewTypeProps } from "../models";

import {
  setDeviceListUpdated,
  setRBMAccessToken,
  setRBMDetails,
  setOrganizations,
  setCustomerType,
  setSiteContext,
  setLogoutIntiated,
  setSiOrgLists,
  setOrgList,
  setHeaderDetails,
  setFilterItems,
  setDeviceListPagination,
  setSoftwareDetails,         //software details action items
  clearSoftwareDetails,
  setSoftwareNotification,
  setSoftwareVersions,
  setTargetForgePoints,
  setSoftwareLogs,
  setAppConfig
} from "../actions";

const initialGlobalState: ViewTypeProps = {
  orgLists: [],
  siOrgLists: [],
  selectedOrg: "",
  gatewayType: "",
  customerOrgId: "",
  siteDeviceId: "",
  siteType: false,
  siteId: "",
  siteName: "",
  siteView: undefined,
  customerName: "",
  rbmAccessToken: "",
  customerType: false,
  logoutIntiated: false,
  deviceListUpdated: false,
  headerDetails: {
    type: '',
    title: '',
    status: '',
    healthStatus: ''
  },
  // filter items for device list
  deviceListFilter: {
    filterItems: {
      status: [],
      gatewaytype: [],
      servesto: [],
      sites: [],
      tag: []
    },
    filterReqObj: '',
    isFilterApplied: false,
    isFilterOpen: false
  },
  // Device List Pagination
  deviceListPagination: {
    page: 1,
    pageSize: 10,
  },
  // Software-specific state
  softwareDetails: {
    showMandatorySoftwareNotification: true,
    softwares: [],
    serviceLogs: [],
    isLoading: false,
    versions: [],
    targetForgePoints: [],
    appConfig: null
  }
};

export const globalReducer = createReducer(
  initialGlobalState,
  (builder: ActionReducerMapBuilder<ViewTypeProps>) => {
    builder
      .addCase(setRBMAccessToken, (state: ViewTypeProps, action: ViewTypeActionProps) => {
        state.rbmAccessToken = action.payload.rbmAccessToken || '';
      })
      .addCase(setRBMDetails, (state: ViewTypeProps, action: ViewTypeActionProps) => {
        state.selectedOrg = action.payload.selectedOrg || '';
        state.rbmAccessToken = action.payload.rbmAccessToken || '';
        state.gatewayType = action.payload.gatewayType || '';
      })
      .addCase(setOrganizations, (state: ViewTypeProps, action: ViewTypeActionProps) => {
        state.orgLists = action.payload.orgLists || [];
      })
      .addCase(setOrgList, (state: ViewTypeProps, action: ViewTypeActionProps) => {
        state.orgLists = action.payload.orgLists || [];
        state.selectedOrg = action.payload.selectedOrg || '';
      })
      .addCase(
        setDeviceListUpdated,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.deviceListUpdated = action.payload.deviceListUpdated || false;
        }
      )
      .addCase(
        setCustomerType,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.customerType = action.payload.customerType;
          state.customerOrgId = action.payload.customerOrgId;
          state.siteDeviceId = action.payload.siteDeviceId;
          state.siteType = action.payload.siteType;
        })
      .addCase(
        setSiteContext,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.siteId = action.payload.siteId || '';
          state.siteName = action.payload.siteName || '';
          state.siteView = action.payload.siteView;
          state.customerName = action.payload.customerName || '';
        })
      .addCase(
        setLogoutIntiated,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.logoutIntiated = action.payload.logoutIntiated;
        }
      )
      .addCase(
        setSiOrgLists,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.siOrgLists = action.payload.siOrgLists || [];
        }
      )
      .addCase(
        setHeaderDetails,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.headerDetails = action.payload.headerDetails || {
            type: '',
            title: '',
            status: '',
            healthStatus: ''
          };
        }
      )
      .addCase(
        setFilterItems,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.deviceListFilter = action.payload.deviceListFilter || {
            filterItems: {
              status: [],
              gatewaytype: [],
              servesto: [],
              sites: [],
              tag: []
            },
            filterReqObj: '',
            isFilterApplied: false        
          };
        }
      )                 
      .addCase(
        setDeviceListPagination,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.deviceListPagination = action.payload.deviceListPagination || {
            page: 1,
            pageSize: 10,
          };
        }
      ) //software details reducers
      .addCase(
        setSoftwareDetails,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          state.softwareDetails = {
            ...state.softwareDetails,
            ...action.payload.softwareDetails
          };
        }
      )
      .addCase(clearSoftwareDetails, (state: ViewTypeProps) => {
          if (state.softwareDetails) {
            state.softwareDetails.softwares = [];
            state.softwareDetails.serviceLogs = [];
            state.softwareDetails.isLoading = false;
            state.softwareDetails.versions = [];
            state.softwareDetails.targetForgePoints = [];
            state.softwareDetails.appConfig = null;
          }
        }
      )
      .addCase(
        setSoftwareNotification,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          if (state.softwareDetails) {
            state.softwareDetails.showMandatorySoftwareNotification = 
              action.payload.softwareDetails?.showMandatorySoftwareNotification ?? true;
          }
        }
      )
      .addCase(
        setSoftwareVersions,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          if (state.softwareDetails) {
            state.softwareDetails.versions = action.payload.softwareDetails?.versions || [];
          }
        }
      )
      .addCase(
        setTargetForgePoints,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          if (state.softwareDetails) {
            state.softwareDetails.targetForgePoints = action.payload.softwareDetails?.targetForgePoints || [];
          }
        }
      )
      .addCase(
        setSoftwareLogs,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          if (state.softwareDetails) {
            state.softwareDetails.serviceLogs = action.payload.softwareDetails?.serviceLogs || [];
          }
        }
      )
      .addCase(
        setAppConfig,
        (state: ViewTypeProps, action: ViewTypeActionProps) => {
          if (state.softwareDetails) {
            state.softwareDetails.appConfig = action.payload.softwareDetails?.appConfig;
          }
        }
      )
  }
);